import { Plane, Mail } from 'lucide-react';
import { InternalLink } from './InternalLink';
import { topicHubs } from '../data/topic-hubs';
import { PrivacySettingsButton } from './AdPrivacy';

interface FooterProps {
  onNavigate?: (path: string) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Plane className="w-6 h-6 text-green-400" />
              <span className="text-xl text-white font-bold">airports.ng</span>
            </div>
            <p className="text-sm mb-4">
              Your complete guide to Nigerian airports, flight information, and travel resources.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4" />
              <a href="mailto:hello@airports.ng" className="hover:text-green-400 transition-colors">
                  Email the airports.ng team
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white mb-4 font-semibold">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <InternalLink to="/airports" onNavigate={onNavigate} className="min-h-[32px] hover:text-green-400 transition-colors cursor-pointer flex items-center">
                  Nigerian Airports
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/passenger-rights" onNavigate={onNavigate} className="min-h-[32px] hover:text-green-400 transition-colors cursor-pointer flex items-center">
                  Passenger Rights
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/blog" onNavigate={onNavigate} className="min-h-[32px] hover:text-green-400 transition-colors cursor-pointer flex items-center">
                  Travel Blog
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/tools" onNavigate={onNavigate} className="min-h-[32px] hover:text-green-400 transition-colors cursor-pointer flex items-center">
                  Travel Tools
                </InternalLink>
              </li>
            </ul>
          </div>

          {/* Travel Hubs */}
          <div>
            <h3 className="text-white mb-4 font-semibold">Travel Hubs</h3>
            <ul className="space-y-2 text-sm">
              {topicHubs.map(hub => (
                <li key={hub.slug}>
                  <InternalLink to={hub.path} onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                    {hub.h1}
                  </InternalLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Travel Tools */}
          <div>
            <h3 className="text-white mb-4 font-semibold">Travel Tools</h3>
            <ul className="space-y-2 text-sm">

              <li>
                <InternalLink to="/flight-calculator" onNavigate={onNavigate} className="min-h-[32px] hover:text-green-400 transition-colors cursor-pointer flex items-center">
                  Flight Time Calculator
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/currency-converter" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Currency Converter
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/checklist" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Travel Checklist
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/quiz" onNavigate={onNavigate} className="min-h-[32px] hover:text-green-400 transition-colors cursor-pointer flex items-center">
                  Airport Quiz
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/game" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Runway Boss Game
                </InternalLink>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white mb-4 font-semibold">Company</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <InternalLink to="/about" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  About Us
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/contact" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Contact
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/privacy" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Privacy Policy
                </InternalLink>
              </li>
              <li>
                <PrivacySettingsButton className="hover:text-green-400 transition-colors" />
              </li>
              <li>
                <InternalLink to="/terms" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Terms of Service
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/advertising" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Advertising
                </InternalLink>
              </li>
              <li>
                <InternalLink to="/editorial-policy" onNavigate={onNavigate} className="hover:text-green-400 transition-colors cursor-pointer">
                  Editorial Policy
                </InternalLink>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 text-center text-sm">
          <p>&copy; {new Date().getFullYear()} airports.ng. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
